<script setup lang="ts">
import { useUiStore, type IframeWindow } from '@/stores/ui';
import {
  XMarkIcon,
  ArrowsPointingOutIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/vue/24/outline';

const uiStore = useUiStore();

function handleRestore(iframe: IframeWindow) {
  uiStore.restoreIframeWindow(iframe.id);
}

function handleClose(iframe: IframeWindow, event: MouseEvent) {
  event.stopPropagation();
  uiStore.closeIframeWindow(iframe.id);
}

function handleOpenInNewTab(iframe: IframeWindow, event: MouseEvent) {
  event.stopPropagation();
  uiStore.openInNewTab(iframe.id);
  uiStore.closeIframeWindow(iframe.id);
}
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition ease-out duration-300"
      enter-from-class="translate-y-full opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition ease-in duration-200"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="translate-y-full opacity-0"
    >
      <div
        v-if="uiStore.minimizedIframes.length > 0 && !uiStore.hasActiveIframe"
        class="fixed bottom-4 left-1/2 -translate-x-1/2 z-40"
      >
        <div class="flex items-center gap-2 px-3 py-2 bg-gray-800/95 dark:bg-dark-800/95 backdrop-blur-lg rounded-2xl shadow-2xl border border-gray-700/50">
          <!-- Label -->
          <span class="text-xs text-gray-400 font-medium px-2 hidden sm:inline">
            Fenêtres ouvertes
          </span>

          <div class="w-px h-6 bg-gray-600 hidden sm:block" />

          <!-- Minimized windows -->
          <div class="flex items-center gap-1.5">
            <div
              v-for="iframe in uiStore.minimizedIframes"
              :key="iframe.id"
              class="group relative flex items-center gap-2 px-3 py-1.5 bg-gray-700/50 hover:bg-gray-600/50 rounded-xl cursor-pointer transition-all duration-200 hover:scale-105"
              @click="handleRestore(iframe)"
            >
              <!-- Favicon -->
              <img
                v-if="iframe.faviconUrl"
                :src="iframe.faviconUrl"
                :alt="iframe.title"
                class="w-4 h-4 rounded"
                @error="($event.target as HTMLImageElement).style.display = 'none'"
              >
              <div
                v-else
                class="w-4 h-4 bg-primary-500 rounded flex items-center justify-center"
              >
                <span class="text-[10px] font-bold text-white">
                  {{ iframe.title.charAt(0).toUpperCase() }}
                </span>
              </div>

              <!-- Title -->
              <span class="text-sm text-white font-medium max-w-[100px] truncate">
                {{ iframe.title }}
              </span>

              <!-- Quick actions (on hover) -->
              <div class="hidden group-hover:flex items-center gap-0.5 ml-1">
                <!-- Open in new tab -->
                <button
                  class="p-1 hover:bg-gray-500/50 rounded transition-colors"
                  title="Ouvrir dans un nouvel onglet"
                  @click="handleOpenInNewTab(iframe, $event)"
                >
                  <ArrowTopRightOnSquareIcon class="w-3.5 h-3.5 text-gray-300 hover:text-white" />
                </button>

                <!-- Close -->
                <button
                  class="p-1 hover:bg-red-500/50 rounded transition-colors"
                  title="Fermer"
                  @click="handleClose(iframe, $event)"
                >
                  <XMarkIcon class="w-3.5 h-3.5 text-gray-300 hover:text-white" />
                </button>
              </div>

              <!-- Expand icon (default state) -->
              <ArrowsPointingOutIcon class="w-3.5 h-3.5 text-gray-400 group-hover:hidden" />
            </div>
          </div>

          <!-- Close all button (if more than 1) -->
          <button
            v-if="uiStore.minimizedIframes.length > 1"
            class="ml-1 px-2 py-1 text-xs text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
            title="Tout fermer"
            @click="uiStore.closeAllIframeWindows()"
          >
            Tout fermer
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
